<template>
	<div
		class="gedit-block-list-edit"
		:class="{
			'is-sorting': dragFromIndex !== null,
			'has-fixed-bottom': fixedBottomBlocks.length > 0 && fixedPlacement === 'body',
		}"
	>
		<template v-if="!scrollBlocks.length">
			<BlockInsertZone
				:index="0"
				@insert="$emit('insert-at', 0)"
			/>
		</template>

		<template v-for="(block, index) in scrollBlocks" :key="block.uid || `${block.id}-${index}`">
			<BlockInsertZone
				:index="index"
				@insert="$emit('insert-at', index)"
			/>
			<BlockEditShell
				v-if="getBlockComponent(block.type)"
				:block-id="block.uid || block.id"
				:active="activeBlockId === (block.uid || block.id)"
				:dragging="dragFromIndex === index"
				:drop-before="dragOverIndex === index && dragFromIndex !== null && dragFromIndex !== index"
				:drop-after="dragOverIndex === scrollBlocks.length && index === scrollBlocks.length - 1 && dragFromIndex !== null && dragFromIndex !== index"
				:actions="getBlockToolbarActions(block.type)"
				:block-data="block.data"
				@drag-start="onDragStart(index, $event)"
				@delete="$emit('delete-block', block.uid || block.id)"
				@action="onBlockAction(block, $event)"
				@gem-color-change="onGemColorChange(block, $event)"
				@theme-color-change="onThemeColorChange(block, $event)"
				@dual-color-change="onDualColorChange(block, $event)"
			>
				<component
					:is="getBlockComponent(block.type)"
					:data="block.data"
					:block-id="block.id"
					editable
					:active="activeBlockId === (block.uid || block.id)"
					@select="$emit('select-block', block.uid || block.id)"
					@update="$emit('update-block', { id: block.uid || block.id, data: $event })"
				/>
			</BlockEditShell>
		</template>

		<BlockInsertZone
			v-if="scrollBlocks.length"
			:index="scrollBlocks.length"
			@insert="$emit('insert-at', scrollBlocks.length)"
		/>

		<Teleport v-if="usePanelFixed" :to="fixedAnchor">
			<div v-if="fixedBottomBlocks.length" class="gedit-block-list-edit__fixed gedit-block-list-edit__fixed--panel">
				<BlockEditShell
					v-for="block in fixedBottomBlocks"
					:key="block.uid || block.id"
					:block-id="block.uid || block.id"
					:active="activeBlockId === (block.uid || block.id)"
					:draggable="false"
					:actions="getBlockToolbarActions(block.type)"
					:block-data="block.data"
					@delete="$emit('delete-block', block.uid || block.id)"
					@action="onBlockAction(block, $event)"
				>
					<component
						:is="getBlockComponent(block.type)"
						:data="block.data"
						:block-id="block.id"
						editable
						:active="activeBlockId === (block.uid || block.id)"
						fixed-bottom
						@select="$emit('select-block', block.uid || block.id)"
						@update="$emit('update-block', { id: block.uid || block.id, data: $event })"
					/>
				</BlockEditShell>
			</div>
		</Teleport>

		<div v-else-if="fixedBottomBlocks.length" class="gedit-block-list-edit__fixed">
			<BlockEditShell
				v-for="block in fixedBottomBlocks"
				:key="block.uid || block.id"
				:block-id="block.uid || block.id"
				:active="activeBlockId === (block.uid || block.id)"
				:draggable="false"
				:actions="getBlockToolbarActions(block.type)"
				:block-data="block.data"
				@delete="$emit('delete-block', block.uid || block.id)"
				@action="onBlockAction(block, $event)"
			>
				<component
					:is="getBlockComponent(block.type)"
					:data="block.data"
					:block-id="block.id"
					editable
					:active="activeBlockId === (block.uid || block.id)"
					fixed-bottom
					@select="$emit('select-block', block.uid || block.id)"
					@update="$emit('update-block', { id: block.uid || block.id, data: $event })"
				/>
			</BlockEditShell>
		</div>
	</div>
</template>

<script>
	import { getBlockComponent } from '../blocks/index.js';
	import { duplicateBlock, getBlockToolbarActions, getBodyFrameIconColors, getGemColors, splitBlocks } from '../templates.js';
	import BlockEditShell from './BlockEditShell.vue';
	import BlockInsertZone from './BlockInsertZone.vue';
	import { reorderBlocks, resolveDropIndex, resolveReorderToIndex } from '../utils/reorderBlocks.js';

	/** PC 编辑页：可编辑的 blocks 列表 */
	export default {
		name: 'GEditBlockListEdit',
		components: { BlockEditShell, BlockInsertZone },
		props: {
			blocks: {
				type: Array,
				default: () => [],
			},
			activeBlockId: {
				type: String,
				default: null,
			},
			fixedPlacement: {
				type: String,
				default: 'body',
			},
			fixedAnchor: {
				type: [Object, HTMLElement],
				default: null,
			},
		},
		emits: ['select-block', 'update-block', 'reorder-blocks', 'delete-block', 'duplicate-block', 'block-action', 'insert-at'],
		data() {
			return {
				dragFromIndex: null,
				dragOverIndex: null,
			};
		},
		computed: {
			scrollBlocks() {
				return splitBlocks(this.blocks).scrollBlocks;
			},
			fixedBottomBlocks() {
				return splitBlocks(this.blocks).fixedBottomBlocks;
			},
			usePanelFixed() {
				return this.fixedPlacement === 'panel' && this.fixedAnchor;
			},
		},
		beforeUnmount() {
			this.teardownDragListeners();
		},
		methods: {
			getBlockComponent,
			getBlockToolbarActions,
			onBlockAction(block, actionKey) {
				if (actionKey === 'copy') {
					this.$emit('duplicate-block', block.uid || block.id);
					return;
				}
				this.$emit('block-action', { id: block.uid || block.id, action: actionKey });
			},
			onGemColorChange(block, { color }) {
				this.$emit('update-block', {
					id: block.uid || block.id,
					data: { gemColor: color },
				});
			},
			onThemeColorChange(block, { color }) {
				this.$emit('update-block', {
					id: block.uid || block.id,
					data: { color },
				});
			},
			onDualColorChange(block, { index, color }) {
				const frameColors = getBodyFrameIconColors(block.data);
				frameColors[index] = color;
				this.$emit('update-block', {
					id: block.uid || block.id,
					data: { frameColors: [...frameColors] },
				});
			},
			onDragStart(index) {
				this.dragFromIndex = index;
				this.dragOverIndex = index;
				document.body.style.userSelect = 'none';
				document.body.style.cursor = 'grabbing';
				document.addEventListener('mousemove', this.onDragMove);
				document.addEventListener('mouseup', this.onDragEnd);
			},
			onDragMove(e) {
				if (this.dragFromIndex === null) return;
				const rects = this.getItemRects();
				this.dragOverIndex = resolveDropIndex(e.clientY, rects);
			},
			onDragEnd() {
				const fromIndex = this.dragFromIndex;
				const insertIndex = this.dragOverIndex;

				this.teardownDragListeners();
				this.dragFromIndex = null;
				this.dragOverIndex = null;

				if (fromIndex === null || insertIndex === null || fromIndex === insertIndex) return;

				const toIndex = resolveReorderToIndex(fromIndex, insertIndex);
				if (fromIndex === toIndex) return;

				const nextScrollBlocks = reorderBlocks(this.scrollBlocks, fromIndex, toIndex);
				this.$emit('reorder-blocks', [...nextScrollBlocks, ...this.fixedBottomBlocks]);
			},
			getItemRects() {
				const shells = this.$el?.querySelectorAll('.gedit-block-list-edit > .g-block-edit-shell') || [];
				return Array.from(shells).map(el => el.getBoundingClientRect());
			},
			teardownDragListeners() {
				document.body.style.userSelect = '';
				document.body.style.cursor = '';
				document.removeEventListener('mousemove', this.onDragMove);
				document.removeEventListener('mouseup', this.onDragEnd);
			},
		},
	};
</script>

<style scoped lang="less">
	@fixed-pay-height: 88px;
	@fixed-pay-inset-x: 12px;
	@fixed-pay-inset-bottom: 12px;

	.gedit-block-list-edit {
		&.has-fixed-bottom {
			padding-bottom: calc(@fixed-pay-height + @fixed-pay-inset-bottom);
		}
	}

	.gedit-block-list-edit.is-sorting {
		cursor: grabbing;

		:deep(.g-block-insert-zone) {
			pointer-events: none;

			.g-block-insert-zone__track {
				opacity: 0;
				visibility: hidden;
			}
		}
	}

	.gedit-block-list-edit__fixed {
		position: absolute;
		left: @fixed-pay-inset-x;
		right: @fixed-pay-inset-x;
		bottom: @fixed-pay-inset-bottom;
		z-index: 5;
	}

	.gedit-block-list-edit__fixed--panel {
		position: relative;
		left: auto;
		right: auto;
		bottom: auto;
		// padding: 0 @fixed-pay-inset-x @fixed-pay-inset-bottom;
		z-index: 10;
	}
</style>

<style lang="less">
	@import '../styles/block-common.less';
</style>
